import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type { MyProfile, TeamMember, TeamMemberFormData, TeamMemberNode } from '../types/team';

class TeamService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * Perfil do usuário logado (cadastro na equipe + subordinados)
   * GET /auth/profile
   */
  async getMyProfile(): Promise<MyProfile> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/auth/profile');
    const data = await readData<MyProfile>(response, 'Falha ao carregar o perfil');
    return {
      ...data,
      subordinates: data?.subordinates ?? [],
      subordinate_ids: data?.subordinate_ids ?? [],
    };
  }

  /**
   * Lista os membros da equipe
   * GET /team-members?include_inactive=&search=
   */
  async getAll(params: { includeInactive?: boolean; search?: string } = {}): Promise<TeamMember[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/team-members${toQuery({ include_inactive: params.includeInactive, search: params.search })}`,
    );
    return readList<TeamMember>(response, 'Falha ao buscar a equipe');
  }

  /**
   * Árvore da hierarquia
   * GET /team-members/tree
   */
  async getTree(): Promise<TeamMemberNode[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/team-members/tree');
    return readList<TeamMemberNode>(response, 'Falha ao buscar a hierarquia');
  }

  /**
   * GET /team-members/:id
   */
  async getById(id: number): Promise<TeamMember> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/team-members/${id}`);
    return readData<TeamMember>(response, 'Falha ao buscar o membro');
  }

  /**
   * POST /team-members
   */
  async create(formData: TeamMemberFormData): Promise<TeamMember> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/team-members', formData);
    return readData<TeamMember>(response, 'Falha ao cadastrar o membro');
  }

  /**
   * PUT /team-members/:id
   */
  async update(id: number, formData: TeamMemberFormData): Promise<TeamMember> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/team-members/${id}`, formData);
    return readData<TeamMember>(response, 'Falha ao atualizar o membro');
  }

  /**
   * Inativa o membro (409 se for gestor com subordinados ativos)
   * DELETE /team-members/:id
   */
  async inactivate(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/team-members/${id}`);
    await ensureOk(response, 'Falha ao inativar o membro');
  }
}

export const teamService = new TeamService();
export default teamService;
